import { AgGridReact } from "ag-grid-react";
import { Copy } from "lucide-react";
import React, { useCallback, useRef } from "react";
import { useImmer } from "use-immer";

import {
  IgAnalyticsTooltip,
  IgIconButton,
  IgToggleButtonGroup,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import {
  AnalyticsAudienceType,
  AnalyzeTinybirdResourceResponseBodyType,
  AudienceFilterDto,
  ExperienceFe,
  GraphConfig,
  VariationFe,
} from "@intelligems/ig-types";
import { Box, ToggleButton, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useGetTinybirdExperienceAnalysis } from "@src/api/analytics/get-tinybird-experience-analysis";
import { AnalyticsAudienceTable } from "@src/features/analytics/tables/analyticsAudienceTable/AnalyticsAudienceTable";
import { useReadOnlyMode } from "@src/features/publicShareLinks/hooks/useReadOnlyMode";
import { useSelector } from "@src/store";
import { getExperienceAnalyticsPageName } from "@src/store/slices/dashboardFilters";
import { selectedExperience } from "@src/store/slices/experiences";
import { newVsReturningCustomersFaq } from "@src/utils/linkSheet";
import { mpAnalyticsViewChanged } from "@src/utils/mixpanel";
import { getAnalyticsPageType } from "@src/utils/mpUtils";
import { openNewTab } from "@src/utils/openNewTab";
import { UseQueryResult } from "@tanstack/react-query";

import { ErrorHandler } from "../../ErrorHandler";

interface AnalyticsSegmentTableWrapperProps {
  experience: ExperienceFe;
  variations: VariationFe[];
  dashboardData: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
  graphConfigs: GraphConfig[];
  testDuration: number;
  metricFilterIndex?: number;
  excludeColumns?: string[];
}

const tabArr = [
  { label: "All Visitors", value: 0 },
  { label: "Desktop/Mobile", value: 1 },
  { label: "New/Returning", value: 2 },
  { label: "Source Channels", value: 3 },
  { label: "Source Sites", value: 4 },
  { label: "Top 10 Countries", value: 5 },
  { label: "Top 10 Landing Pages", value: 6 },
  { label: "Purchase Likelihood", value: 7 },
];

const toolTipData: Record<number, string> = {
  0: "",
  1: "Compares performance by desktop vs. mobile visitors. Mobile includes tablet traffic.",
  2: "Compares performance by visitors who have visited your site before vs. visitors who are brand new. Note that this table is visitors, and not customers. More details here",
  3: "Compares performance by traffic channel.",
  4: "Compares performance by traffic site.",
  5: "Compares performance by country. Shows top 10 by visitor count.",
  6: "Compares performance by landing page. Shows top 10 by visitor count.",
  7: "Compares performance by purchase likelihood tier.",
};

const tabValueToQueryType: Record<number, AnalyticsAudienceType> = {
  1: "device_type",
  2: "visitor_type",
  3: "source_channel",
  4: "source_site",
  5: "country_code",
  6: "landing_page_full_path",
  7: "purchase_likelihood",
};

export const hasPurchaseLikelihoodAudienceFilter = (
  filters: AudienceFilterDto[] | undefined | null
): boolean =>
  Boolean(
    filters?.some((filter) =>
      filter.expression?.some(
        (node) => node.query?.type === "purchaseLikelihood"
      )
    )
  );

export const AnalyticsAudienceTableWrapper = ({
  experience,
  variations,
  dashboardData,
  graphConfigs,
  testDuration,
  metricFilterIndex,
  excludeColumns,
}: AnalyticsSegmentTableWrapperProps) => {
  const theme = useTheme();
  const { isReadOnly } = useReadOnlyMode();
  const expPageName = useSelector(getExperienceAnalyticsPageName);
  const currentExperience = useSelector(selectedExperience);
  const [tabValue, setTabValue] = useImmer<number>(0);
  const tableRef = useRef<AgGridReact>(null);
  const formattedGraphConfigs: GraphConfig[] = graphConfigs.map((config) => ({
    ...config,
    datasetId:
      expPageName === "postTestMetrics"
        ? "variation_post_metrics_audience"
        : "variation_audience",
  }));

  const hasError = dashboardData[0].error;

  const hasPurchaseLikelihoodTargeting = hasPurchaseLikelihoodAudienceFilter(
    currentExperience?.audience?.filters
  );
  const visibleTabs = hasPurchaseLikelihoodTargeting
    ? tabArr
    : tabArr.filter((tab) => tab.value !== 7);

  const segmentDataQuery: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[] =
    useGetTinybirdExperienceAnalysis(
      {
        graphConfigs: formattedGraphConfigs,
        experience,
        audience: tabValueToQueryType[tabValue] ?? "device_type",
        topN: [5, 6].includes(tabValue) ? 10 : undefined,
        overviewDataLoaded: dashboardData[0].isSuccess,
        enabled: tabValue !== 0,
      },
      "experiences"
    );

  const renderTooltipContent = (value: number): React.ReactNode | undefined => {
    if (value === 2) {
      return (
        <>
          Compares performance by visitors who have visited your site before vs.
          visitors who are brand new. Note that this table is visitors, and not
          customers.{" "}
          <span
            style={{ textDecoration: "underline" }}
            // variant="body2"
            onClick={() => {
              openNewTab(
                newVsReturningCustomersFaq,
                "new vs returning customers"
              );
            }}
          >
            More details here.
          </span>
        </>
      );
    }

    return toolTipData[value];
  };

  const copyToClipboard = useCallback(() => {
    if (tableRef.current) {
      const visibleColumns = tableRef.current.api?.getAllDisplayedColumns();
      let csvContent = "";
      if (visibleColumns?.length > 0) {
        const headers = visibleColumns.map((column) => {
          const colDef = column.getColDef();
          return colDef.headerName || colDef.field;
        });
        csvContent += headers.join("\t") + "\n";
      }

      const rowData: any[] = [];
      tableRef.current.api?.forEachNodeAfterFilterAndSort((node) => {
        if (node.group) {
          const rowValues = visibleColumns.map((column, index) => {
            return index === 0 ? node.key : "";
          });
          rowData.push(rowValues.join("\t"));
        } else if (node.data) {
          const rowValues = visibleColumns.map((column) => {
            const field = column.getColDef().field;
            if (field && node.data[field] !== undefined) {
              if (field === "variation_id") {
                return variations.find(
                  (variation) => variation.id === node.data[field]
                )?.name;
              } else {
                const valueFormatterFunc: any =
                  column.getColDef()?.valueFormatter;
                if (valueFormatterFunc) {
                  return valueFormatterFunc({ value: node.data[field] });
                } else return node.data[field]?.value || "";
              }
            } else return "";
          });
          rowData.push(rowValues.join("\t"));
        }
      });

      csvContent += rowData.join("\n");

      navigator.clipboard.writeText(csvContent).catch((err) => {
        console.error("Failed to copy: ", err);
      });
    }
  }, []);

  return (
    <Box
      sx={{
        borderRadius: "10px",
        backgroundColor: "white",
        border: `1px solid ${theme.igBorders.core.default}`,
        flexWrap: "nowrap",
      }}
    >
      <Grid
        container
        sx={{
          padding: "20px 30px",
          borderBottom: `1px solid ${theme.igBorders.core.default}`,
        }}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Grid marginRight={"30px"}>
          <IgTypography fontWeight={600} variant={"h2"}>
            By Audience
          </IgTypography>
        </Grid>

        {!hasError && (
          <>
            <Grid sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
              <IgToggleButtonGroup
                control={{
                  exclusive: true,
                  onChange: (event, value: string) => {
                    if (value) {
                      setTabValue(parseInt(value));
                      const audienceType = tabValueToQueryType[parseInt(value)];
                      if (audienceType) {
                        mpAnalyticsViewChanged({
                          pageType: getAnalyticsPageType(
                            currentExperience?.category ?? "experiment"
                          ),
                          pageName: expPageName,
                          changeType: "audience_filter",
                          filterChange: audienceType,
                        });
                      }
                    }
                  },
                }}
                value={tabValue.toString()}
                sx={{ zIndex: "1000 !important", height: "28px" }}
                buttons={visibleTabs.map(({ label, value }) => {
                  const tooltip = renderTooltipContent(value);

                  const button = (
                    <ToggleButton
                      key={value}
                      value={value.toString()}
                      sx={{
                        borderRadius: "30px",
                        width: "auto",
                        height: "28px",
                      }}
                    >
                      {label}
                    </ToggleButton>
                  );

                  return (
                    <IgTooltip key={value} title={tooltip}>
                      {button}
                    </IgTooltip>
                  );
                })}
              />
            </Grid>
            {!isReadOnly && (
              <Grid>
                <IgAnalyticsTooltip title={"Copy Table Data"}>
                  <IgIconButton
                    disabled={
                      dashboardData[0].isLoading || dashboardData[0].isPending
                    }
                    onClick={copyToClipboard}
                  >
                    <Copy size={20} />
                  </IgIconButton>
                </IgAnalyticsTooltip>
              </Grid>
            )}
          </>
        )}
      </Grid>
      {hasError ? (
        <ErrorHandler
          text={`An error occurred while loading data for this section.`}
        />
      ) : (
        <AnalyticsAudienceTable
          ref={tableRef}
          tabValue={tabValue}
          experience={experience}
          variations={variations}
          dashboardData={dashboardData}
          segmentDataQuery={segmentDataQuery}
          graphConfigs={graphConfigs}
          testDuration={testDuration}
          metricFilterIndex={metricFilterIndex}
          audienceSegmentSelected={tabValueToQueryType[tabValue]}
          excludeColumns={excludeColumns}
        />
      )}
    </Box>
  );
};
